/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import {
	ISchemaBuilderAnyModel,
	SCHEMA_BUILDER_ANY_VALUE_TYPE,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { InspectModel } from "../InspectModel";
import { FieldDropdown, TFieldDropdownOption } from "../../Fields/FieldDropdown";
import { useEditorInspectorContext } from "../../../contexts/EditorInspectorContext";

export const ControlSchemaBuilderAny_Primary: TInspectorControlComponent<ISchemaBuilderAnyModel> = ({
	model: _model,
	id
}) => {
	const editorInspectorCtx = useEditorInspectorContext();
	const { model, validationErrors } = useBlueprintModel(_model, "builderAny");

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setType(model, value as SCHEMA_BUILDER_ANY_VALUE_TYPE, undefined, true);
		},
		[ model ]
	);

	const typeOptions = useMemo(() => {
		const items: TFieldDropdownOption[] = [
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.STRING,
				label: "String",
				description: "Text data.",
				icon: "mdi/text-short"
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.PASSWORD,
				label: "Password",
				description: "Password value that will be encrypted",
				icon: "mdi/lock"
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.INTEGER,
				label: "Integer",
				description: "Number without decimals",
				icon: "mdi/numeric"
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.FLOAT,
				label: "Float",
				description: "Number with decimal places",
				icon: "mdi/numeric"
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.BOOLEAN,
				label: "Boolean",
				description: "True or false",
				icon: "mdi/checkbox-marked-outline"
			},
			// @todo Enable when date will be implemented
			// {
			// 	value: SCHEMA_BUILDER_ANY_VALUE_TYPE.DATE,
			// 	label: "Date",
			// 	icon: "mdi/calendar-clock"
			// },
			{
				isSeparator: true
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.ENUM_STRING,
				label: "Enum: String",
				description: "Select from text values",
				icon: "mdi/format-list-bulleted"
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.ENUM_INTEGER,
				label: "Enum: Integer",
				description: "Select from numberic values without decimals",
				icon: "mdi/format-list-bulleted"
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.ENUM_FLOAT,
				label: "Enum: Float",
				description: "Select from numeric values with decimals",
				icon: "mdi/format-list-bulleted"
			},
			{
				isSeparator: true
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.OBJECT,
				label: "Object",
				description: "Key-value data with fixed properties.",
				icon: "mdi/format-list-bulleted-type"
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.ARRAY,
				label: "Array",
				description: "List of values",
				icon: "mdi/code-brackets"
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.MAP,
				label: "Map",
				description: "Key-value pairs with dynamic text keys",
				icon: "mdi/code-braces"
			},
			{
				isSeparator: true
			},
			{
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.ANY,
				label: "Any",
				description: "Any type",
				icon: "mdi/help-rhombus"
			}
		];

		return items;
	}, []);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<FieldDropdown
			id={id}
			value={model.type}
			options={typeOptions}
			onChange={handleChange}
			validationErrors={validationErrors}
			readOnly={editorInspectorCtx?.readOnly}
			grow={true}
		/>
	);
};

export const ControlSchemaBuilderAny_Children: TInspectorControlComponent<ISchemaBuilderAnyModel> = ({
	model: _model,
	path,
	depth
}) => {
	const { model } = useBlueprintModel(_model, "builderAny");
	const { model: valueModel } = useBlueprintModel(_model?.value);

	if (!model || !valueModel) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			<InspectModel model={valueModel} path={path.concat([ "value" ])} depth={depth} />
		</>
	);
};

export const ControlSchemaBuilderAny = (model: TGenericModelNode): IInspectorControls => ({
	primary: {
		component: ControlSchemaBuilderAny_Primary,
		model: model
	},
	children: {
		component: ControlSchemaBuilderAny_Children,
		model: model
	}
	// displayInSection: true
});
